import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, User, Phone, Search, Check, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { PasswordInput } from '../ui/PasswordInput';
import { apiClient } from '../../lib/api/client';

interface Manager {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface ProfileCompletionModalProps {
  isOpen: boolean;
  userEmail: string;
  onComplete: () => void;
  onClose: () => void;
}

const profileCompletionSchema = z.object({
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number'),
  confirmPassword: z.string(),
  phoneNumber: z.string()
    .min(10, 'Phone number must be at least 10 digits')
    .regex(/^[\+]?[1-9][\d]{0,15}$/, 'Please enter a valid phone number'),
  managerId: z.string().optional()
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ProfileCompletionFormData = z.infer<typeof profileCompletionSchema>;

export const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({
  isOpen,
  userEmail,
  onComplete,
  onClose
}) => {
  const [managers, setManagers] = useState<Manager[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedManager, setSelectedManager] = useState<Manager | null>(null);
  const [showManagerDropdown, setShowManagerDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<ProfileCompletionFormData>({
    resolver: zodResolver(profileCompletionSchema)
  });

  // Load managers on component mount
  useEffect(() => {
    if (isOpen) {
      fetchManagers('');
    }
  }, [isOpen]);

  // Search managers when query changes
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const debounceTimer = setTimeout(() => {
        fetchManagers(searchQuery);
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else if (searchQuery.length === 0) {
      fetchManagers('');
    }
  }, [searchQuery]);

  // Update form when manager is selected
  useEffect(() => {
    if (selectedManager) {
      setValue('managerId', selectedManager.id);
      setSearchQuery(selectedManager.name);
      setShowManagerDropdown(false);
    }
  }, [selectedManager, setValue]);

  // Close manager dropdown on outside click or touch
  useEffect(() => {
    if (!showManagerDropdown) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowManagerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showManagerDropdown]);

  const fetchManagers = async (query: string) => {
    try {
      setSearching(true);
      const results = await apiClient.searchManagers(query);
      setManagers(results);
    } catch (error) {
      console.error('Failed to fetch managers:', error);
      toast.error('Failed to load managers');
    } finally {
      setSearching(false);
    }
  };

  const onSubmit = async (data: ProfileCompletionFormData) => {
    try {
      setLoading(true);

      // If the account has no password yet, set it first (requires auth via OTP)
      try {
        await apiClient.setPassword({ new_password: data.newPassword });
      } catch (e) {
        // ignore if already set
      }
      
      await apiClient.completeUserProfile({
        email: userEmail,
        newPassword: data.newPassword,
        phoneNumber: data.phoneNumber,
        managerId: data.managerId
      });

      toast.success('Profile completed successfully!');
      onComplete();
    } catch (error: any) {
      console.error('Profile completion failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  const handleManagerSelect = (manager: Manager) => {
    setSelectedManager(manager);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowManagerDropdown(true);
    
    if (!query) {
      setSelectedManager(null);
      setValue('managerId', '');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-xl mr-4">
              <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Complete Your Profile
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please complete your profile to continue
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Logout
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6">
          {/* Guidance banner */}
          <div className="mb-4 bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200 rounded-lg p-3 text-sm">
            If you logged in using OTP, please set your password here. You’ll use this password for future logins.
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* User Email Display */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Email Address
              </label>
              <div className="flex items-center text-gray-900 dark:text-white">
                <User className="w-4 h-4 mr-2" />
                {userEmail}
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <label htmlFor="newPassword" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                New Password *
              </label>
              <PasswordInput
                id="newPassword"
                placeholder="Enter your new password"
                {...register('newPassword')}
                className={errors.newPassword ? 'border-red-500 focus:border-red-500 focus:shadow-glow-error' : ''}
                error={errors.newPassword?.message}
              />
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Must be at least 8 characters with uppercase, lowercase, and number
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Confirm Password *
              </label>
              <PasswordInput
                id="confirmPassword"
                placeholder="Confirm your new password"
                {...register('confirmPassword')}
                className={errors.confirmPassword ? 'border-red-500 focus:border-red-500 focus:shadow-glow-error' : ''}
                error={errors.confirmPassword?.message}
              />
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <label htmlFor="phoneNumber" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Contact Number *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="phoneNumber"
                  type="tel"
                  placeholder="+91XXXXXXXXXX"
                  defaultValue={'+91'}
                  className={`pl-10 ${errors.phoneNumber ? 'border-red-500 focus:border-red-500 focus:shadow-glow-error' : ''}`}
                  {...register('phoneNumber')}
                />
              </div>
              {errors.phoneNumber && (
                <div className="flex items-center text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.phoneNumber.message}
                </div>
              )}
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Include country code if international (e.g., +1, +91)
              </div>
            </div>

            {/* Manager Selection */}
              <div className="space-y-2">
              <label htmlFor="manager" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Your Manager (Optional)
              </label>
              <div className="relative" ref={dropdownRef}>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search for your manager (optional)..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => setShowManagerDropdown(true)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setShowManagerDropdown(false); }}
                  className={`pl-10 ${errors.managerId ? 'border-red-500 focus:border-red-500 focus:shadow-glow-error' : ''}`}
                />
                
                {/* Manager Dropdown */}
                {showManagerDropdown && (
                  <div className="absolute top-full left-0 right-0 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                    {/* Allow no manager selection */}
                    <div
                      className="p-3 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-100 dark:border-gray-600"
                      onClick={() => {
                        setSelectedManager(null);
                        setValue('managerId', undefined);
                        setSearchQuery('');
                        setShowManagerDropdown(false);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">No manager</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Proceed without assigning a manager</div>
                        </div>
                        {!selectedManager && (
                          <Check className="w-5 h-5 text-green-500" />
                        )}
                      </div>
                    </div>
                    {searching ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        Searching managers...
                      </div>
                    ) : managers.length > 0 ? (
                      managers.map((manager) => (
                        <div
                          key={manager.id}
                          className="p-3 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                          onClick={() => handleManagerSelect(manager)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {manager.name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {manager.email} • {manager.role}
                              </div>
                            </div>
                            {selectedManager?.id === manager.id && (
                              <Check className="w-5 h-5 text-green-500" />
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        No managers found
                      </div>
                    )}
                  </div>
                )}
              </div>
              {errors.managerId && (
                <div className="flex items-center text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.managerId.message}
                </div>
              )}
              <div className="text-xs text-gray-500 dark:text-gray-400">
                You can skip this step and add your manager later from your profile settings.
              </div>
            </div>

            {/* Selected Manager Display */}
            {selectedManager && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                <div className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-2" />
                  <div>
                    <div className="font-medium text-green-800 dark:text-green-200">
                      Selected Manager
                    </div>
                    <div className="text-sm text-green-600 dark:text-green-300">
                      {selectedManager.name} ({selectedManager.email})
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-between items-center pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Logout Instead
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="px-8 py-3"
              >
                {loading ? 'Completing Profile...' : 'Complete Profile'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionModal;
